/* eslint-disable jsdoc/require-jsdoc */
import { Update } from '@ngrx/entity';
import { createAction, props } from '@ngrx/store';
import { ProcedurePrescription } from '@mona/models';

export const loadProcedurePrescriptions = createAction('[ProcedurePrescription/API] Load ProcedurePrescriptions');

export const processProcedurePrescriptions = createAction(
    '[ProcedurePrescription/API] Process ProcedurePrescriptions',
    props<{ procedurePrescriptions: ProcedurePrescription[] }>(),
);

export const loadProcedurePrescriptionsFailure = createAction(
    '[ProcedurePrescription/API] Load ProcedurePrescriptions Failure',
    props<{ error: any }>(),
);

/**
 * Emitted when an in-flight `loadProcedurePrescriptions` is aborted because the selected
 * encounter was cleared mid-fetch (typical during logout/login or rapid encounter switch).
 * Lets the reducer drop the `isLoading: true` flag instead of leaving the procedure
 * form's save-button spinner stuck on forever — the customer-reported "endless circle on
 * save" symptom from a hospital terminal was the same shape as the daily-goals
 * stuck spinner already fixed under MD-9750: the previous `takeUntil(isEmptyEncounterId)`
 * silently completed the inner observable without dispatching anything, and the reducer's
 * `clearProcedurePrescriptions` / `clearEncounter` handler only reset `error` while
 * leaving `isLoading` truthy. The procedure form's `ui-card` then waited indefinitely on
 * `selectProcedurePrescriptionsIsLoading`. Refs: MD-9750.
 */
export const loadProcedurePrescriptionsCancelled = createAction(
    '[ProcedurePrescription/API] Load ProcedurePrescriptions Cancelled',
    props<{ reason: string }>(),
);

export const addProcedurePrescription = createAction(
    '[ProcedurePrescription/API] Add ProcedurePrescription',
    props<{ procedurePrescription: ProcedurePrescription }>(),
);

export const upsertProcedurePrescription = createAction(
    '[ProcedurePrescription/API] Upsert ProcedurePrescription',
    props<{ procedurePrescription: ProcedurePrescription }>(),
);

export const addProcedurePrescriptions = createAction(
    '[ProcedurePrescription/API] Add ProcedurePrescriptions',
    props<{ procedurePrescriptions: ProcedurePrescription[] }>(),
);

export const upsertProcedurePrescriptions = createAction(
    '[ProcedurePrescription/API] Upsert ProcedurePrescriptions',
    props<{ procedurePrescriptions: ProcedurePrescription[] }>(),
);

export const updateProcedurePrescription = createAction(
    '[ProcedurePrescription/API] Update ProcedurePrescription',
    props<{ update: Update<ProcedurePrescription> }>(),
);

export const updateProcedurePrescriptions = createAction(
    '[ProcedurePrescription/API] Update ProcedurePrescriptions',
    props<{ updates: Update<ProcedurePrescription>[] }>(),
);

export const handleDeleteProcedurePrescription = createAction(
    '[ProcedurePrescription/API] Handle Delete ProcedurePrescription',
    props<{ procedurePrescription: ProcedurePrescription }>(),
);

export const clearProcedurePrescriptions = createAction('[ProcedurePrescription/API] Clear ProcedurePrescriptions');

export const handleConfirmChanges = createAction('[ProcedurePrescription/API] Handle Confirm Changes');

export const addProcedurePrescriptionsChanges = createAction(
    '[ProcedurePrescription/API] Add Changes',
    props<{ toUpdateEntities: ProcedurePrescription[]; toRemoveIds: string[] }>(),
);
